import type { Bytes, FileName, Time } from "@pdfking/structs"
import type { StringGUID } from "@pdfking/tfunc"
import { AtomHash, GuidToString } from "@pdfking/tfunc"

/**
 * What identifies an entry when nobody wrote an etag for it.
 *
 * Size and write time are what every storage already knows, and what a caller
 * comparing two reads of the same name is actually asking about — a weak etag,
 * and honest about it. Content nobody hashed is not going to be hashed here.
 *
 * `key` is what the entry is called, and it is not optional: etags share one
 * space per storage, so two entries of the same length written in the same
 * second would otherwise be one id — and a caller keyed by that id, a registry
 * mapping an entry back to what produced it, would answer for one with the
 * other. Everything that derives an etag knows a name for what it is deriving
 * it about: a filesystem has the path, a download has the url it came from.
 *
 * Naming an entry still hashes nothing — the name and the directory entry are
 * all a store knows without opening the file. Bytes that move to another name
 * are another entry, and the store that wants them to be the same one is a
 * content store, whose key is its hash and which says so itself.
 *
 * @param size - Size of the entry in bytes
 * @param modified - When the entry was written, in unix seconds
 * @param key - What the entry is called within whatever holds it
 * @returns A stable id for that entry
 */
export const ContentETag = (size: Bytes, modified: Time, key: FileName): StringGUID =>
  GuidToString(AtomHash([key, size, modified]))
